import { clampDay, monthGrid, parseDateText, shiftMonth, shiftMonthDay } from '../calc/datefield.js';
import { esc, fmtMonth } from '../calc/format.js';
import { shiftDay } from '../calc/periods.js';
import { el } from './dom.js';

/* --------------------------------------------------------------------------
   Datumfält med svensk kalender

   Datumfälten är textfält med ÅÅÅÅ-MM-DD, inte type=date: ett datumfält
   visar datumet i datorns format, 10/13/2026 med engelska som språk, och
   kalendern börjar på söndag. Här står datumet alltid som i Sverige.

   Kunden kan skriva datumet — 2026-03-12, 2026-3-12 och 20260312 går — eller
   välja det i kalendern bakom knappen bredvid fältet. Ett datum utanför filen
   blir filens första eller sista dag, och något som inte är ett datum ger
   tillbaka det som stod där. Båda förklaras under fälten (#dateHint).
   Filens första och sista dag står i fältets data-min och data-max.

   Kalendern är en dialog under fältet, måndag först, på svenska. Med
   tangentbord: pilarna flyttar en dag eller en vecka, Home och End till
   veckans början och slut, Page Up och Page Down en månad, Enter eller
   mellanslag väljer och Escape stänger. Fokus stannar i kalendern tills den
   stängs och går sedan tillbaka till knappen. Dagar utanför filen går inte
   att välja.
   -------------------------------------------------------------------------- */

const WEEKDAYS      = ['Mån','Tis','Ons','Tor','Fre','Lör','Sön'];
const WEEKDAYS_LONG = ['måndag','tisdag','onsdag','torsdag','fredag','lördag','söndag'];
const MONTHS_LONG   = ['januari','februari','mars','april','maj','juni','juli','augusti','september','oktober','november','december'];

const ICON = '<svg aria-hidden="true" focusable="false" width="16" height="16" viewBox="0 0 16 16">' +
  '<rect x="1.5" y="2.5" width="13" height="12" rx="1.5" fill="none" stroke="currentColor" stroke-width="1.4"/>' +
  '<path d="M1.5 6.5h13M5 1v3M11 1v3" stroke="currentColor" stroke-width="1.4" fill="none"/></svg>';

/** "torsdag 12 mars 2026" — dagens namn för skärmläsare. */
function dayName(iso){
  const [y, m, d] = iso.split('-').map(Number);
  const wd = (new Date(Date.UTC(y, m - 1, d)).getUTCDay() + 6) % 7;
  return `${WEEKDAYS_LONG[wd]} ${d} ${MONTHS_LONG[m - 1]} ${y}`;
}

/** Sätter filens första och sista dag på datumfälten. */
export function setDateBounds(inputs, min, max){
  for(const i of inputs){ i.dataset.min = min; i.dataset.max = max; }
}

function hint(text){
  const h = el('dateHint');
  if(!h) return;
  h.textContent = text;
}

/**
 * Gör ett textfält till datumfält: lägger till kalenderknappen och tolkar det
 * kunden skriver. Anropas innan sidans egna change-lyssnare kopplas, så att
 * de alltid får ett giltigt datum. Ger knappen.
 */
export function initDateField(input){
  const wrap = document.createElement('span');
  wrap.className = 'datefield';
  input.replaceWith(wrap);
  wrap.appendChild(input);
  const btn = document.createElement('button');
  btn.type = 'button';
  btn.className = 'date-btn';
  btn.setAttribute('aria-label', 'Kalender: ' + input.getAttribute('aria-label'));
  btn.setAttribute('aria-haspopup', 'dialog');
  btn.setAttribute('aria-expanded', 'false');
  btn.innerHTML = ICON;
  wrap.appendChild(btn);

  // Det som stod i fältet innan kunden började skriva
  input.addEventListener('focus', () => { input.dataset.prev = input.value; });
  input.addEventListener('change', () => {
    const {min, max} = input.dataset;
    const raw = input.value.trim();
    if(raw === ''){ input.value = ''; hint(''); return; }
    const iso = parseDateText(raw);
    if(!iso){
      input.value = input.dataset.prev ?? '';
      hint(`”${raw}” är inget datum. Skriv datumet som år-månad-dag, till exempel ${max || '2026-03-12'}.`);
      return;
    }
    const c = clampDay(iso, min, max);
    input.value = input.dataset.prev = c;
    hint(c === iso ? '' : `Filen har kvitton från ${min} till ${max}, så datumet är ändrat till ${c}.`);
  });
  btn.addEventListener('click', () => {
    const same = cal && cal.btn === btn;
    close(false);
    if(!same) open(input, btn);
  });
  return btn;
}

let cal = null;   // {input, btn, box, month, focus}

/** Stänger en öppen kalender. Anropas också när sidan ritas om. */
export function closeCalendar(){ close(false); }

function close(returnFocus){
  if(!cal) return;
  const {btn, box} = cal;
  cal = null;
  box.remove();
  btn.setAttribute('aria-expanded', 'false');
  if(returnFocus && btn.isConnected) btn.focus();
}

function open(input, btn){
  const {min, max} = input.dataset;
  const start = parseDateText(input.value) || max;
  if(!start) return;   // ingen fil inläst
  const focus = clampDay(start, min, max);
  const box = document.createElement('div');
  box.className = 'cal';
  box.setAttribute('role', 'dialog');
  box.setAttribute('aria-label', input.getAttribute('aria-label'));
  document.body.appendChild(box);
  cal = {input, btn, box, month: focus.slice(0, 7), focus};
  render();

  const r = btn.closest('.datefield').getBoundingClientRect();
  const width = box.offsetWidth || 300;
  box.style.left = Math.max(16, Math.min(r.left + window.scrollX, window.scrollX + window.innerWidth - width - 16)) + 'px';
  box.style.top = (r.bottom + window.scrollY + 6) + 'px';
  btn.setAttribute('aria-expanded', 'true');

  box.addEventListener('click', onClick);
  box.addEventListener('keydown', onKey);
  focusDay();
}

function render(){
  const {input, month, focus} = cal;
  const {min, max} = input.dataset;
  const chosen = parseDateText(input.value);
  const canPrev = !min || shiftMonth(month, -1) >= min.slice(0, 7);
  const canNext = !max || shiftMonth(month, 1) <= max.slice(0, 7);
  const title = fmtMonth(month);

  cal.box.innerHTML = `
    <div class="cal-head">
      <button type="button" class="cal-nav" data-nav="-1" aria-label="Föregående månad"${canPrev ? '' : ' disabled'}>‹</button>
      <div class="cal-title" id="calTitle" aria-live="polite">${esc(title.charAt(0).toUpperCase() + title.slice(1))}</div>
      <button type="button" class="cal-nav" data-nav="1" aria-label="Nästa månad"${canNext ? '' : ' disabled'}>›</button>
    </div>
    <table class="cal-grid" aria-labelledby="calTitle">
      <thead><tr>${WEEKDAYS.map((d, i) => `<th scope="col" abbr="${WEEKDAYS_LONG[i]}">${d}</th>`).join('')}</tr></thead>
      <tbody>${monthGrid(month).map(w => '<tr>' + w.map(iso => {
        if(!iso) return '<td></td>';
        const out = (min && iso < min) || (max && iso > max);
        return `<td><button type="button" class="cal-day" data-day="${iso}" tabindex="${iso === focus ? 0 : -1}"` +
          ` aria-label="${dayName(iso)}"${iso === chosen ? ' aria-pressed="true"' : ''}${out ? ' disabled' : ''}>` +
          `${Number(iso.slice(8))}</button></td>`;
      }).join('') + '</tr>').join('')}</tbody>
    </table>`;
}

function focusDay(){
  cal.box.querySelector(`.cal-day[data-day="${cal.focus}"]`)?.focus();
}

/** Flyttar markeringen till day, inom filen, och byter månad vid behov. */
function moveTo(day){
  const {min, max} = cal.input.dataset;
  cal.focus = clampDay(day, min, max);
  cal.month = cal.focus.slice(0, 7);
  render();
  focusDay();
}

function choose(day){
  const {input} = cal;
  input.value = input.dataset.prev = day;
  hint('');
  close(true);
  input.dispatchEvent(new Event('change', {bubbles: true}));
}

function onClick(e){
  const nav = e.target.closest('.cal-nav');
  if(nav && !nav.disabled){
    const n = Number(nav.dataset.nav);
    const {min, max} = cal.input.dataset;
    cal.month = shiftMonth(cal.month, n);
    cal.focus = clampDay(shiftMonthDay(cal.focus, n), min, max);
    render();
    // Knappen ritades om; fokus stannar på den, eller på dagen om den nu är avstängd
    const again = cal.box.querySelector(`.cal-nav[data-nav="${n}"]`);
    if(again && !again.disabled) again.focus(); else focusDay();
    return;
  }
  const day = e.target.closest('.cal-day');
  if(day && !day.disabled) choose(day.dataset.day);
}

function onKey(e){
  if(e.key === 'Escape'){ e.preventDefault(); e.stopPropagation(); close(true); return; }
  if(e.key === 'Tab'){
    // Fokus stannar i kalendern: bläddringsknapparna och den markerade dagen
    const stops = [...cal.box.querySelectorAll('.cal-nav:not([disabled])'), cal.box.querySelector('.cal-day[tabindex="0"]')]
      .filter(Boolean);
    const i = stops.indexOf(document.activeElement);
    e.preventDefault();
    stops[(i + (e.shiftKey ? -1 : 1) + stops.length) % stops.length]?.focus();
    return;
  }
  if(!e.target.classList.contains('cal-day')) return;
  const d = cal.focus;
  const wd = (new Date(d + 'T00:00:00Z').getUTCDay() + 6) % 7;
  const step = {
    ArrowLeft: () => shiftDay(d, -1), ArrowRight: () => shiftDay(d, 1),
    ArrowUp:   () => shiftDay(d, -7), ArrowDown:  () => shiftDay(d, 7),
    Home:      () => shiftDay(d, -wd), End: () => shiftDay(d, 6 - wd),
    PageUp:    () => shiftMonthDay(d, e.shiftKey ? -12 : -1),
    PageDown:  () => shiftMonthDay(d, e.shiftKey ? 12 : 1)
  }[e.key];
  if(step){ e.preventDefault(); moveTo(step()); }
}

/** Kopplar händelserna en gång för hela sidan. */
export function initCalendar(){
  // Klick utanför stänger. pointerdown, så att ett klick i ett annat fält
  // inte först hinner flytta fokus från en kalender som står kvar.
  document.addEventListener('pointerdown', e => {
    if(cal && !cal.box.contains(e.target) && !cal.btn.contains(e.target)) close(false);
  });
  // Kalendern är placerad efter fältets läge; ändras fönstret stämmer det inte längre
  window.addEventListener('resize', () => close(false));
}
